// src/encounter/lib/to-chart-labs.ts
//
// Resolves a LAB chart piece — `labs.set:<id>` (a catalogue lab set, `lab-set-catalogue.ts`) or
// `labs.latest:<LOINC>` — from the reads the chart's
// own Labs tab makes (`patients/services/use-patient-lab-results.ts`: the lab desk list, then each
// lab request's Reports read). No new endpoint. Synchronous, from reads already made, so an
// insertion is one transaction (spec §4.3), exactly like `vitals.latest` and `allergies.list`.
//
// WHAT A LINE IS. One observation from one report — the latest CURRENT result of that test, by
// collection time (a result with a collection time always beats one without). Two laboratories'
// results are never joined into one line, and nothing is
// computed: the value and unit print as the lab wrote them, the flag is the lab's own
// interpretation verbatim (else the flag printed on an uploaded report, as the Labs tab shows it), the date is the COLLECTION date (the observation's effective time). When
// the record has no collection date the line says "reported …, collection date not recorded" — a
// report date is never shown as if it were a collection date.
//
// HONEST STATES (R6), stricter here because "none recorded" is a clinical statement:
//   loading  the list or any report read has not come back;
//   error    the list failed, any report read failed (a PARTIAL read), the results store is not
//            available, or a row or report names another patient (G5);
//   empty    ONLY when nothing could have been missed: every report read came back, the list was
//            read whole (not truncated), and every current observation carries a LOINC code — an
//            uncoded observation might be the very test asked for, so its presence makes "none"
//            unprovable and the link stays unresolved instead;
//   ok       at least one line, and no uncoded observation that could be newer than a quoted line
//            (undated, or collected at or after it) — else "latest" is unprovable: error.

import type { GetPatientsLabDetailResponse, PatientLabOrderDetail } from '@/types/scheduling-api';
import type { LabObservation, LabReport, LabRequestReports } from '@/types/lab-results-api';
import {
  asUtcInstant,
  formatLabRange,
  formatLabValue,
  isAmendedStatus,
  isPreliminaryStatus,
  isWithdrawn,
  labCollectedAt,
  labDateText,
  labFlagCode,
  labInstant,
  uniqueLabRequests,
} from '@/patients/lib/lab-results';
import { labSetId, labTestId, latestLabCode, wallClockDate, type ChartQuery, type ChartResult, type ChartSnapshot } from './note-pieces';
import { LAB_SET_CATALOGUE_VERSION, labSet, labTest, type CarriedField } from './lab-set-catalogue';
import type { ChartRead } from './to-chart-snapshot';

/** One lab request's Reports read. `unavailable` = the results endpoints answered 404 (not deployed). */
export interface LabReportRead {
  labRequestId: string;
  status: 'loading' | 'error' | 'unavailable' | 'ok';
  data?: LabRequestReports;
}

export interface NoteLabReads {
  /** The lab desk list rows for this patient, one per lab request. */
  requests: PatientLabOrderDetail[];
  /** The list has pages that were not read. */
  truncated: boolean;
  /** One per request, in the same order. */
  reports: LabReportRead[];
}

/** The lab request ids a list read holds, de-duplicated (the list joins document files). */
export function labRequestIdsOf(list: GetPatientsLabDetailResponse | undefined): string[] {
  return uniqueLabRequests(list?.patientLabOrderDetails).map((r) => r.id as string);
}

/** The list read + its report reads → one read state. `reports` is aligned with `labRequestIdsOf`. */
export function buildNoteLabReads(
  list: { data?: GetPatientsLabDetailResponse; isError?: boolean },
  reports: ReadonlyArray<{ data?: LabRequestReports; isError?: boolean; error?: unknown } | undefined>,
  notDeployed: (error: unknown) => boolean,
): ChartRead<NoteLabReads> {
  if (list.isError) return { status: 'error' };
  if (list.data === undefined) return { status: 'loading' };
  const requests = uniqueLabRequests(list.data.patientLabOrderDetails);
  const pages = list.data.totalNumberOfPages ?? 1;
  return {
    status: 'ok',
    data: {
      requests,
      truncated: pages > (list.data.pageNumber ?? 1),
      reports: requests.map((r, i) => {
        const q = reports[i];
        const labRequestId = r.id as string;
        if (q?.isError) return { labRequestId, status: notDeployed(q.error) ? 'unavailable' : 'error' };
        return q?.data === undefined ? { labRequestId, status: 'loading' } : { labRequestId, status: 'ok', data: q.data };
      }),
    },
  };
}

interface Found {
  obs: LabObservation;
  report: LabReport;
  request: PatientLabOrderDetail | undefined;
}

/** When the report was issued, as a UTC instant (the results API's zone-less UTC marked: lab-results.ts). */
const reportedAtOf = (f: Found): string | undefined => asUtcInstant(f.report.issuedAt);

/** When the specimen was collected — never the API's default copy of the issue time (lab-results.ts). */
const collectedAtOf = (f: Found): string | undefined => labCollectedAt(f.obs.effectiveAt, f.report.issuedAt);

const time = labInstant;

/**
 * Latest by COLLECTION time. A result with a collection time always wins over one without (Codex
 * review: ordering an undated result by its report time would let it displace a result whose
 * collection date is known). Only when no result has a collection time is the latest REPORT time
 * used to choose — and the line then says "reported …, collection date not recorded".
 */
function latest(found: Found[]): Found | undefined {
  // An undated result's collection time is -Infinity, so once ANY result is dated it can never win.
  const anyDated = found.some((f) => time(collectedAtOf(f)) !== -Infinity);
  const at = (f: Found) => (anyDated ? time(collectedAtOf(f)) : time(reportedAtOf(f)));
  return [...found].sort((a, b) => at(b) - at(a) || time(reportedAtOf(b)) - time(reportedAtOf(a)))[0];
}

/** "12 Sep 2026" (D-45), on the clinic's calendar. */
export function dateText(iso: string): string {
  return wallClockDate(iso) ?? iso;
}

function toResult({ obs, report, request }: Found, line: LabLine): ChartResult {
  const label = obs.display?.trim() || line.label || obs.loinc?.trim() || 'Lab result';
  const value = formatLabValue(obs);
  const unit = obs.unit?.trim() || undefined;
  // The same flag the chart Labs table shows (`labFlagCode`): an uploaded report's printed flag when unset.
  const flag = line.carry.includes('flag') ? labFlagCode(obs) : undefined;
  const printed = line.carry.includes('range') ? formatLabRange(obs) : '—';
  const range = printed !== '—' ? printed : undefined;
  // The chart's Labs tab names the vendor the same way (`buildLabResultGroup`): the request's, else the report's.
  const lab = request?.labVendorName?.trim() || report.labVendorName?.trim() || undefined;
  const collectedAt = collectedAtOf({ obs, report, request });
  const reportedAt = reportedAtOf({ obs, report, request });
  const precision = collectedAt ? obs.effectiveAtPrecision?.trim() || undefined : undefined;
  const when = collectedAt
    ? `collected ${labDateText(collectedAt, precision) ?? dateText(collectedAt)}`
    : reportedAt
      ? `reported ${dateText(reportedAt)}, collection date not recorded`
      : 'date not recorded';
  // The current version is the one quoted (a superseded version is not current); its status is said.
  const status =
    isPreliminaryStatus(obs.status) || isPreliminaryStatus(report.status)
      ? ', preliminary'
      : isAmendedStatus(obs.status) || isAmendedStatus(report.status)
        ? ', amended'
        : '';
  return {
    ...(obs.id ? { sourceId: obs.id } : {}),
    display: `${label} ${value}${unit ? ` ${unit}` : ''}${range ? ` (lab range ${range})` : ''}${flag ? `, lab flag ${flag}` : ''}${status} — ${when}, ${lab ?? 'lab not recorded'}`,
    label,
    value,
    ...(unit ? { unit } : {}),
    ...(obs.loinc?.trim() ? { code: obs.loinc.trim() } : {}),
    ...(lab ? { lab } : {}),
    ...(flag ? { flag } : {}),
    ...(collectedAt ? { collectedAt } : {}),
    ...(precision ? { collectedAtPrecision: precision } : {}),
    ...(reportedAt ? { reportedAt } : {}),
    ...(range ? { range } : {}),
  };
}

type LabAnswer = Pick<ChartSnapshot, 'status' | 'results' | 'catalogueVersion'>;
const LOADING: LabAnswer = { status: 'loading', results: [] };
const ERROR: LabAnswer = { status: 'error', results: [] };

interface LabLine {
  label?: string;
  loinc: readonly string[];
  carry: readonly CarriedField[];
}

/** The lines a lab query asks for: every line of its catalogue set, its one catalogue test, or the one
 *  LOINC code it names. */
function linesFor(query: ChartQuery): readonly LabLine[] | undefined {
  const setId = labSetId(query);
  if (setId !== undefined) return labSet(setId)?.lines;
  const testId = labTestId(query);
  if (testId !== undefined) {
    const test = labTest(testId);
    return test ? [test.line] : undefined;
  }
  const code = latestLabCode(query);
  return code ? [{ loinc: [code], carry: ['flag', 'range'] }] : undefined;
}

export function resolveLabQuery(
  query: ChartQuery,
  patientId: string,
  read: ChartRead<NoteLabReads> | undefined,
): LabAnswer {
  const answer = resolveLines(query, patientId, read);
  // A catalogue set's or test's quote records which catalogue version defined it at the time.
  const fromCatalogue = labSet(labSetId(query) ?? '') !== undefined || labTest(labTestId(query) ?? '') !== undefined;
  return fromCatalogue ? { ...answer, catalogueVersion: LAB_SET_CATALOGUE_VERSION } : answer;
}

function resolveLines(query: ChartQuery, patientId: string, read: ChartRead<NoteLabReads> | undefined): LabAnswer {
  const lines = linesFor(query);
  // An unknown set (a newer catalogue's) or a malformed query: nothing can be read — never "none".
  if (!lines) return { ...ERROR, results: [] };
  if (!read || read.status === 'loading') return { ...LOADING, results: [] };
  if (read.status === 'error' || !read.data) return { ...ERROR, results: [] };
  const { requests, reports, truncated } = read.data;
  // G5: a row or a report naming another patient is never quoted into this patient's note.
  if (requests.some((r) => r.patientId && r.patientId !== patientId)) return { ...ERROR, results: [] };
  if (reports.some((r) => r.status === 'loading')) return { ...LOADING, results: [] };
  // A report read that says ok but carries no body was not read either (R6, defended here too).
  if (reports.some((r) => r.status === 'error' || r.status === 'unavailable' || (r.status === 'ok' && !r.data))) return { ...ERROR, results: [] };
  const answered = reports.filter((r): r is LabReportRead & { data: LabRequestReports } => !!r.data);
  if (answered.some((r) => r.data.storeAvailable === false)) return { ...ERROR, results: [] };
  if (answered.some((r) => r.data.patientId && r.data.patientId !== patientId)) return { ...ERROR, results: [] };

  const found: Found[] = [];
  for (const r of answered) {
    const request = requests.find((q) => q.id === r.labRequestId);
    for (const report of r.data.reports ?? []) {
      if (report.isCurrent === false || isWithdrawn(report.status)) continue;
      for (const obs of report.observations ?? []) {
        if (obs.isCurrent === false || isWithdrawn(obs.status) || formatLabValue(obs) === '—') continue;
        found.push({ obs, report, request });
      }
    }
  }
  const chosen = lines
    .map((line) => ({ line, found: latest(found.filter((f) => line.loinc.includes(f.obs.loinc?.trim() ?? ''))) }))
    .filter((c): c is { line: LabLine; found: Found } => !!c.found);
  // An observation with no LOINC code might be the very test asked for. It makes "none" unprovable,
  // and it makes "latest" unprovable for any chosen line it could be newer than — undated, or
  // collected at or after that line (Codex review, second pass). Either way the link stays unresolved.
  const uncoded = found.filter((f) => !f.obs.loinc?.trim());
  const outranked = uncoded.some(
    (u) => time(collectedAtOf(u)) === -Infinity || chosen.some((c) => time(collectedAtOf(u)) >= time(collectedAtOf(c.found))),
  );
  if (chosen.length) return outranked ? { ...ERROR, results: [] } : { status: 'ok', results: chosen.map((c) => toResult(c.found, c.line)) };
  return truncated || uncoded.length ? { ...ERROR, results: [] } : { status: 'empty', results: [] };
}
